import React, { useEffect, useMemo } from "react";
import {
  Card,
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListSubheader,
  SelectChangeEvent,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import DateFormInput from "../DateFormInput";
import { FieldData, PurchaseOrderForm } from "../../types";
import { yupResolver } from "@hookform/resolvers/yup";
import { getFieldsData } from "../../api/purchaseOrders/purchaseOrders";
import dayjs from "dayjs";

const purchaseOrderValidation = yup.object({
  administrativeData: yup.object({
    purchaseOrderNumber: yup.string(),
    customizedProjectCode: yup.string(),
    salesforceOpp: yup.string(),
    date: yup.date(),
    country: yup.string(),
    responsiblePerson: yup.string(),
    companyName: yup.string(),
    forMilitaryUse: yup
      .string()
      .required("For military use is required")
      .oneOf(["true", "false"], "Invalid option"),
  }),
  marketingData: yup.object({
    endUserName: yup.string().required("End-user name is required"),
    endUserCountry: yup.string().required("End-user country is required"),
    projectName: yup.string(),
    marketSegments: yup.object({
      name: yup.string(),
      option: yup.string(),
    }),
  }),
});

interface CartPurchaseOrderDetailssProps {
  onChange?: (data: PurchaseOrderForm) => void;
  setIsValid: (isValid: boolean) => void;
}

const CartPurchaseOrderDetails: React.FC<CartPurchaseOrderDetailssProps> = ({
  onChange,
  setIsValid,
}) => {
  const [fieldData, setFieldData] = React.useState<FieldData | null>(null);
  const [hasReadCache, setHasReadCache] = React.useState(false);

  const [loading, setLoading] = React.useState(false);

  const {
    watch,
    register,
    setValue,
    subscribe,
    reset,
    formState: { isValid },
  } = useForm<PurchaseOrderForm>({
    resolver: yupResolver(purchaseOrderValidation),
    mode: "onChange",
  });

  const date = watch("administrativeData.date") || null;

  const handleGetFieldData = async () => {
    setLoading(true);
    try {
      const response = await getFieldsData();
      const sortedItems: FieldData = {
        marketSegments: response.marketSegments,
        countries: response.countries.sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      };
      setFieldData(sortedItems);
    } catch (error) {
      console.error("Error fetching field data:", error);
    } finally {
      setLoading(false);
    }
  };

  const marketSegments = useMemo(() => {
    const options: JSX.Element[] = [
      <MenuItem key="default" value=""></MenuItem>,
    ];

    fieldData?.marketSegments.forEach((segment) => {
      options.push(
        <ListSubheader key={segment.name}>{segment.name}</ListSubheader>,
      );
      segment.options.forEach((option) => {
        options.push(
          <MenuItem key={option} value={`${option} - ${segment.name}`}>
            {option}
          </MenuItem>,
        );
      });
    });
    return options;
  }, [fieldData]);

  const handleMarketSegments = (event: SelectChangeEvent<string>) => {
    const [option, name] = event.target.value.split(" - ");
    setValue("marketingData.marketSegments.name", name.trim());
    setValue("marketingData.marketSegments.option", option.trim());
  };

  const renderValue = (value: string) => value;

  const storeValues = (values: PurchaseOrderForm) => {
    const storedData = localStorage.getItem("@powerprotection_cartFormData");
    const purchaseOrderData = storedData ? JSON.parse(storedData) : {};

    const updatedData = {
      ...purchaseOrderData,
      ...values,
    };

    localStorage.setItem(
      "@powerprotection_cartFormData",
      JSON.stringify(updatedData),
    );
  };

  const resetForm = () => {
    localStorage.removeItem("@powerprotection_cartFormData");
    reset(
      { administrativeData: { date: dayjs().toDate() }, marketingData: { marketSegments: { name: "", option: "" } } },
      { keepDefaultValues: false },
    );
  };

  const loadValuesFromCache = () => {
    const storedData = localStorage.getItem("@powerprotection_cartFormData");

    if (storedData) {
      const purchaseOrderData = JSON.parse(storedData);
      Object.keys(purchaseOrderData).forEach((key) => {
        setValue(key as any, purchaseOrderData[key], {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      });
    }

    setTimeout(() => {
      setHasReadCache(true);
    }, 100);
  };

  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
        dirtyFields: true,
      },
      callback: ({ values }) => {
        onChange?.(values);

        if (hasReadCache) {
          storeValues(values);
        }
      },
    });

    return () => callback();
  }, [subscribe, hasReadCache, onChange]);

  useEffect(() => {
    handleGetFieldData();

    setValue("administrativeData.date", dayjs().toDate());

    loadValuesFromCache();
  }, []);

  useEffect(() => {
    setIsValid(isValid);
  }, [isValid]);

  return (
    <Card variant="outlined" sx={{ overflow: "unset" }}>
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" color="common.black">
              Purchase order details
            </Typography>

            <Button
              variant="text"
              color="primary"
              onClick={resetForm}
              title="Reset and clear saved data"
            >
              Reset
            </Button>
          </Stack>

          <Typography
            variant="bodyLargeWeightRegular"
            color="grey.600"
            sx={{ marginBottom: "16px" }}
          >
            Before downloading enter your data to continue.
          </Typography>
        </Box>

        <form>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="administrative-data-content"
              id="administrative-data-header"
            >
              <Typography variant="bodyLargeWeightMedium" color="common.black">
                Administrative data
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing="8px" sx={{ marginBottom: "16px" }}>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.purchaseOrderNumber"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Purchase order number
                  </Typography>
                  <TextField
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-administrativeData.purchaseOrderNumber",
                    }}
                    {...register("administrativeData.purchaseOrderNumber")}
                    type="text"
                    fullWidth
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.customizedProjectCode"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Customized project code
                  </Typography>
                  <TextField
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-administrativeData.customizedProjectCode",
                    }}
                    {...register("administrativeData.customizedProjectCode")}
                    type="text"
                    fullWidth
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.salesforceOpp"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Salesforce opp. ref / name
                  </Typography>
                  <TextField
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-administrativeData.salesforceOpp",
                    }}
                    {...register("administrativeData.salesforceOpp")}
                    type="text"
                    fullWidth
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    paddingTop: "12px !important",
                  }}
                >
                  <Typography variant="bodySmallWeightRegular" color="grey.600">
                    Date
                  </Typography>
                  <DateFormInput
                    value={date}
                    {...register("administrativeData.date")}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.country"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Country
                  </Typography>
                  <Select
                    {...register("administrativeData.country")}
                    value={watch("administrativeData.country") || ""}
                    startAdornment={
                      loading ? (
                        <CircularProgress color="secondary" size={20} />
                      ) : undefined
                    }
                    disabled={loading}
                    displayEmpty
                  >
                    {fieldData?.countries.map(({ name }) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.responsiblePerson"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Responsible person
                  </Typography>
                  <TextField
                    {...register("administrativeData.responsiblePerson")}
                    type="text"
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-administrativeData.responsiblePerson",
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.companyName"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Company name
                  </Typography>
                  <TextField
                    {...register("administrativeData.companyName")}
                    type="text"
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-administrativeData.companyName",
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-administrativeData.forMilitaryUse"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                    sx={{
                      display: "block",
                      marginBottom: "4px",
                      paddingTop: "5px",
                    }}
                  >
                    For military use
                    <Typography
                      variant="bodySmallWeightRegular"
                      sx={(theme) => ({ color: theme.palette.secondary.main })}
                    >
                      {" "}
                      *
                    </Typography>
                  </Typography>
                  <Select
                    {...register("administrativeData.forMilitaryUse")}
                    value={watch("administrativeData.forMilitaryUse") || ""}
                    displayEmpty
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="marketing-data-content"
              id="marketing-data-header"
            >
              <Typography variant="bodyLargeWeightMedium" color="common.black">
                Marketing data
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing="8px">
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-marketingData.endUserName"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    End-user name
                    <Typography
                      variant="bodySmallWeightRegular"
                      sx={(theme) => ({ color: theme.palette.secondary.main })}
                    >
                      {" "}
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    {...register("marketingData.endUserName")}
                    type="text"
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-marketingData.endUserName",
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-marketingData.endUserCountry"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    End-user country
                    <Typography
                      variant="bodySmallWeightRegular"
                      sx={(theme) => ({ color: theme.palette.secondary.main })}
                    >
                      {" "}
                      *
                    </Typography>
                  </Typography>
                  <Select
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-marketingData.endUserCountry",
                    }}
                    {...register("marketingData.endUserCountry")}
                    value={watch("marketingData.endUserCountry") || ""}
                    startAdornment={
                      loading ? (
                        <CircularProgress color="secondary" size={20} />
                      ) : undefined
                    }
                    disabled={loading}
                    displayEmpty
                  >
                    {fieldData?.countries.map(({ name }) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-marketingData.projectName"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Project name
                  </Typography>
                  <TextField
                    {...register("marketingData.projectName")}
                    type="text"
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-marketingData.projectName",
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography
                    id="mui-component-select-marketingData.marketSegments"
                    variant="bodySmallWeightRegular"
                    color="grey.600"
                  >
                    Market segments
                  </Typography>
                  <Select
                    SelectDisplayProps={{
                      "aria-labelledby":
                        "mui-component-select-marketingData.marketSegments",
                    }}
                    inputProps={{
                      "aria-labelledby":
                        "mui-component-select-marketingData.marketSegments",
                    }}
                    MenuProps={{
                      "aria-labelledby":
                        "mui-component-select-marketingData.marketSegments",
                    }}
                    onChange={handleMarketSegments}
                    renderValue={renderValue}
                    startAdornment={
                      loading ? (
                        <CircularProgress color="secondary" size={20} />
                      ) : undefined
                    }
                    value={
                      marketSegments.find((option) => {
                        const optionValue = option.props.value;
                        return optionValue ===
                          `${watch("marketingData.marketSegments.option")} - ${watch(
                            "marketingData.marketSegments.name",
                          )}`
                          ? optionValue
                          : "";
                      })?.props.value || ""
                    }
                    disabled={loading}
                    displayEmpty
                  >
                    {marketSegments}
                  </Select>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </form>
      </Box>
    </Card>
  );
};

export default CartPurchaseOrderDetails;
